const CART_UPDATE_EVENT = 'cart:update';
const CONNECTED_PRODUCT_SWAPPED_EVENT = 'fp:connected-product-swapped';
const SAMPLE_ADDED_EVENT = 'fp:sample-added';
const SAMPLE_ADD_PENDING_EVENT = 'fp:sample-add-pending';
const SAMPLE_ADD_FAILED_EVENT = 'fp:sample-add-failed';
const REFRESH_TIMEOUT_MS = 4_000;
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const DRAG_CLOSE_MIN_PX = 96;

function rootUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

function isSampleItem(item) {
  return Boolean(item?.properties?._is_sample === 'true');
}

function sampleQuantity(cart) {
  if (!Array.isArray(cart?.items)) return 0;

  return cart.items.reduce((total, item) => {
    return isSampleItem(item) ? total + Math.max(0, Number(item.quantity) || 0) : total;
  }, 0);
}

function hasRegularItems(cart) {
  return Array.isArray(cart?.items) && cart.items.some((item) => !isSampleItem(item));
}

function sampleVariantIds(cart) {
  const ids = new Set();

  if (!Array.isArray(cart?.items)) return ids;

  cart.items.forEach((item) => {
    if (!isSampleItem(item)) return;
    const variantId = item.variant_id ?? item.id;
    if (variantId != null) ids.add(String(variantId));
  });

  return ids;
}

function sampleItemsByVariant(cart) {
  const items = new Map();

  if (!Array.isArray(cart?.items)) return items;

  cart.items.forEach((item) => {
    if (!isSampleItem(item)) return;
    const variantId = item.variant_id ?? item.id;
    if (variantId != null) items.set(String(variantId), item);
  });

  return items;
}

function cartSectionIds() {
  return document.getElementById('cart-icon-bubble') ? ['cart-icon-bubble'] : [];
}

function previewThemeId() {
  const currentValue = new URL(window.location.href).searchParams.get('preview_theme_id');
  if (currentValue && /^\d+$/.test(currentValue)) return currentValue;

  const contextValue = String(window.__fpConnectedProductRequestContext?.previewThemeId || '');
  return /^\d+$/.test(contextValue) ? contextValue : '';
}

class FPSampleWidget extends HTMLElement {
  #cartAbortController = null;
  #cartSequence = 0;
  #refreshAbortController = null;
  #refreshSequence = 0;
  #lastTrigger = null;
  #suppressFocusRestore = false;
  #dragPointerId = null;
  #dragStartY = 0;
  #dragDistance = 0;
  #lastCart = null;
  #pendingAddVariants = new Set();
  #pendingRemoveVariants = new Set();
  #mutationQueue = Promise.resolve();

  connectedCallback() {
    this.#dialog?.addEventListener('close', this.#onDialogClose);
    this.addEventListener(SAMPLE_ADDED_EVENT, this.#onSampleAdded);
    this.addEventListener(SAMPLE_ADD_PENDING_EVENT, this.#onSampleAddPending);
    this.addEventListener(SAMPLE_ADD_FAILED_EVENT, this.#onSampleAddFailed);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('submit', this.#onSubmit);
    document.addEventListener(CART_UPDATE_EVENT, this.#onCartUpdate);
    document.addEventListener(CONNECTED_PRODUCT_SWAPPED_EVENT, this.#onConnectedProductSwapped);
  }

  disconnectedCallback() {
    this.#dialog?.removeEventListener('close', this.#onDialogClose);
    this.removeEventListener(SAMPLE_ADDED_EVENT, this.#onSampleAdded);
    this.removeEventListener(SAMPLE_ADD_PENDING_EVENT, this.#onSampleAddPending);
    this.removeEventListener(SAMPLE_ADD_FAILED_EVENT, this.#onSampleAddFailed);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener(CART_UPDATE_EVENT, this.#onCartUpdate);
    document.removeEventListener(CONNECTED_PRODUCT_SWAPPED_EVENT, this.#onConnectedProductSwapped);
    this.#removeDragListeners();
    this.#cartAbortController?.abort();
    this.#refreshAbortController?.abort();
  }

  open() {
    const dialog = this.#dialog;
    if (!dialog) return;

    this.#lastTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#hydrate();
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('overflow-hidden');
    this.#syncCart();
  }

  close() {
    if (this.#dialog?.open) this.#dialog.close();
  }

  get #dialog() {
    const dialog = this.querySelector('[data-sample-widget-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  #hydrate() {
    if (this.dataset.sampleHydrated === 'true') return;

    const template = this.querySelector('[data-sample-widget-template]');
    const list = this.querySelector('[data-sample-widget-list]');
    if (!(template instanceof HTMLTemplateElement) || !(list instanceof HTMLElement)) return;

    list.replaceChildren(template.content.cloneNode(true));
    this.#sortGroups();
    list.setAttribute('aria-busy', 'true');
    this.dataset.sampleHydrated = 'true';
  }

  #sortGroups() {
    this.querySelectorAll('[data-sample-group-list]').forEach((list) => {
      const cards = Array.from(list.querySelectorAll(':scope > [data-sample-card]'));
      cards
        .sort((left, right) => Number(left.dataset.sampleSort) - Number(right.dataset.sampleSort))
        .forEach((card) => list.append(card));
    });
  }

  async #requestCart(signal) {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal,
    });

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  async #fetchCart() {
    this.#cartAbortController?.abort();
    this.#cartAbortController = new AbortController();
    return this.#requestCart(this.#cartAbortController.signal);
  }

  async #syncCart() {
    if (this.dataset.sampleHydrated !== 'true') return;

    const sequence = ++this.#cartSequence;
    this.#setPending(true);

    try {
      const cart = await this.#fetchCart();
      if (sequence !== this.#cartSequence) return;
      this.#applyCart(cart);
    } catch (error) {
      if (sequence !== this.#cartSequence) return;
      if (error?.name === 'AbortError') return;
      console.warn('[FP] Sample widget cart check failed.', error);
      this.#blockAll(this.dataset.cartCheckMessage || 'Não foi possível verificar seu carrinho agora.');
    } finally {
      if (sequence === this.#cartSequence) this.#setPending(false);
    }
  }

  #applyCart(cart) {
    this.#lastCart = cart;

    const limit = Number(this.dataset.sampleLimit) || 5;
    const sampleItems = sampleItemsByVariant(cart);
    const addedVariants = sampleVariantIds(cart);
    addedVariants.forEach((variantId) => this.#pendingAddVariants.delete(variantId));

    let quantity = sampleQuantity(cart);
    this.#pendingRemoveVariants.forEach((variantId) => {
      const item = sampleItems.get(variantId);
      quantity -= Math.max(0, Number(item?.quantity) || 0);
    });
    const confirmedQuantity = Math.max(0, quantity);
    this.#pendingAddVariants.forEach((variantId) => {
      if (!addedVariants.has(variantId)) quantity += 1;
    });
    quantity = Math.max(0, quantity);

    const regularItemsPresent = hasRegularItems(cart);
    const limitReached = quantity >= limit;

    this.querySelectorAll('[data-sample-product-form]').forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;

      const variantId = String(new FormData(form).get('id') || '');
      const button = form.querySelector('[data-sample-add-button]');
      const label = form.querySelector('[data-sample-button-text]');
      const removeButton = form.querySelector('[data-sample-remove-button]');
      if (!(button instanceof HTMLButtonElement)) return;

      const isPendingAdd = this.#pendingAddVariants.has(variantId);
      const isPendingRemove = this.#pendingRemoveVariants.has(variantId);
      const isAdded = (addedVariants.has(variantId) && !isPendingRemove) || isPendingAdd;
      const isBlocked = isAdded || isPendingRemove || regularItemsPresent || limitReached;

      button.dataset.sampleAdded = isAdded ? 'true' : 'false';
      button.dataset.samplePending = isPendingAdd ? 'true' : 'false';
      button.dataset.sampleBlocked = isBlocked ? 'true' : 'false';
      button.disabled = isBlocked;

      if (label) {
        label.textContent = isAdded ? this.dataset.addedLabel || 'Adicionada ✓' : 'Adicionar';
      }

      if (removeButton instanceof HTMLButtonElement) {
        const canRemove = addedVariants.has(variantId) && !isPendingAdd && !isPendingRemove;
        removeButton.dataset.sampleVisible = canRemove ? 'true' : 'false';
        removeButton.dataset.samplePending = isPendingRemove ? 'true' : 'false';
        removeButton.dataset.sampleCartKey = String(sampleItems.get(variantId)?.key || '');
        removeButton.disabled = !canRemove;
        removeButton.setAttribute('aria-hidden', canRemove ? 'false' : 'true');
        removeButton.tabIndex = canRemove ? 0 : -1;
      }
    });

    this.#updateCounter(quantity, limit, confirmedQuantity);

    if (regularItemsPresent) {
      this.#showStatus(
        this.dataset.conflictMessage ||
          'Você já tem produtos no carrinho. Esvazie o carrinho antes de adicionar amostras.'
      );
    } else if (limitReached) {
      this.#showStatus(this.dataset.limitMessage || `Você atingiu o limite de ${limit} amostras por pedido.`);
    } else {
      this.#showStatus('');
    }
  }

  #onSampleAddPending = (event) => {
    const variantId = String(event.detail?.variantId || '');
    if (!variantId) return;

    this.#pendingAddVariants.add(variantId);
    this.#showStatus('');
    if (this.#lastCart) this.#applyCart(this.#lastCart);
  };

  #onSampleAddFailed = (event) => {
    const variantId = String(event.detail?.variantId || '');
    if (variantId) this.#pendingAddVariants.delete(variantId);
    if (this.#lastCart) this.#applyCart(this.#lastCart);

    const message = String(event.detail?.message || '');
    if (message) this.#showStatus(message);
  };

  #setPending(isPending) {
    const list = this.querySelector('[data-sample-widget-list]');
    if (list) list.setAttribute('aria-busy', isPending ? 'true' : 'false');

    if (!isPending) return;

    this.querySelectorAll('[data-sample-add-button]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.dataset.sampleBlocked = 'true';
      button.disabled = true;
    });
  }

  #blockAll(message) {
    this.querySelectorAll('[data-sample-add-button]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.dataset.sampleBlocked = 'true';
      button.disabled = true;
    });
    this.#showStatus(message);
  }

  #updateCounter(quantity, limit, confirmedQuantity = quantity) {
    const counter = this.querySelector('[data-sample-widget-counter]');
    if (counter) {
      counter.textContent = `${quantity}/${limit}`;
      counter.setAttribute('aria-label', `${quantity} de ${limit} amostras`);
    }

    const selection = this.querySelector('[data-sample-widget-selection]');
    if (selection) selection.textContent = `${quantity} de ${limit}`;

    const cartLink = this.querySelector('[data-sample-widget-cart-link]');
    if (cartLink instanceof HTMLAnchorElement) {
      const isEnabled = confirmedQuantity > 0;
      cartLink.hidden = !isEnabled;
      cartLink.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');
      cartLink.tabIndex = isEnabled ? 0 : -1;
    }
  }

  #showStatus(message, type = 'error') {
    const status = this.querySelector('[data-sample-widget-status]');
    if (!(status instanceof HTMLElement)) return;

    status.textContent = message;
    status.dataset.statusType = type;
    status.hidden = !message;
  }

  #onSampleAdded = (event) => {
    const variantId = String(event.detail?.variantId || '');
    if (variantId) this.#pendingAddVariants.delete(variantId);

    const cart = event.detail?.cart;
    if (Array.isArray(cart?.items)) {
      this.#applyCart(cart);
      const limit = Number(this.dataset.sampleLimit) || 5;
      const remaining = Math.max(0, limit - sampleQuantity(cart));
      if (remaining > 0 && !hasRegularItems(cart)) {
        this.#showStatus(`Amostra adicionada. Você pode escolher mais ${remaining}.`, 'success');
      }
      return;
    }

    this.#syncCart();
  };

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-sample-product-form]')) return;

    event.preventDefault();
    const button = form.querySelector('[data-sample-add-button]');
    const variantId = String(new FormData(form).get('id') || '');
    if (!(button instanceof HTMLButtonElement) || !variantId || button.disabled) return;

    form.dispatchEvent(
      new CustomEvent(SAMPLE_ADD_PENDING_EVENT, {
        bubbles: true,
        detail: { variantId },
      })
    );

    this.#mutationQueue = this.#mutationQueue
      .then(() => this.#submitSampleForm(form, variantId))
      .catch((error) => {
        console.warn('[FP] Sample widget mutation queue failed.', error);
      });
  };

  async #submitSampleForm(form, variantId) {
    try {
      const cart = await this.#requestCart();
      const limit = Math.max(1, Number(form.dataset.sampleLimit) || 5);

      if (hasRegularItems(cart)) {
        throw new Error(
          form.dataset.conflictErrorMessage ||
            'Você já tem produtos no carrinho. Esvazie o carrinho antes de adicionar amostras.'
        );
      }

      if (sampleVariantIds(cart).has(variantId)) {
        form.dispatchEvent(
          new CustomEvent(SAMPLE_ADDED_EVENT, {
            bubbles: true,
            detail: { cart, variantId },
          })
        );
        return;
      }

      if (sampleQuantity(cart) >= limit) {
        throw new Error(
          form.dataset.limitErrorMessage || `Você atingiu o limite de ${limit} amostras por pedido.`
        );
      }

      const body = new FormData(form);
      const sectionIds = cartSectionIds();
      if (sectionIds.length > 0) {
        body.set('sections', sectionIds.join(','));
        body.set('sections_url', window.location.pathname + window.location.search);
      }

      const response = await fetch(`${rootUrl()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      });
      const addPayload = await response.json().catch(() => null);
      if (!response.ok || addPayload?.status) {
        throw new Error(
          addPayload?.description || addPayload?.message || 'Não foi possível adicionar esta amostra. Tente novamente.'
        );
      }

      const updatedCart = await this.#requestCart();
      this.#renderCartBubble(addPayload?.sections);
      this.#dispatchCartUpdate(updatedCart, variantId, 'sample-widget-add');
      form.dispatchEvent(
        new CustomEvent(SAMPLE_ADDED_EVENT, {
          bubbles: true,
          detail: { cart: updatedCart, variantId },
        })
      );
    } catch (error) {
      console.warn('[FP] Sample widget add failed.', error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : form.dataset.cartCheckErrorMessage ||
            'Não foi possível verificar seu carrinho agora. Tente novamente em instantes.';
      form.dispatchEvent(
        new CustomEvent(SAMPLE_ADD_FAILED_EVENT, {
          bubbles: true,
          detail: { message, variantId },
        })
      );
    }
  }

  #renderCartBubble(sections) {
    const html = sections?.['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (!html || !bubble) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const content = parsed.querySelector('.shopify-section');
    if (content) bubble.innerHTML = content.innerHTML;
  }

  #dispatchCartUpdate(cart, variantId, source) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source,
        productVariantId: variantId,
        cartData: cart,
      });
    }

    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: this.id || 'fp-sample-widget',
          data: {
            source,
            itemCount: Number(cart?.item_count) || 0,
            sections: cart?.sections || {},
            variantId,
          },
        },
      })
    );
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-sample-widget-launcher]')) {
      this.open();
      return;
    }

    if (event.target.closest('[data-sample-widget-close]') || event.target === this.#dialog) {
      this.close();
      return;
    }

    const cartLink = event.target.closest('[data-sample-widget-cart-link]');
    if (cartLink instanceof HTMLAnchorElement && cartLink.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    const filterButton = event.target.closest('[data-sample-filter]');
    if (filterButton instanceof HTMLButtonElement && this.contains(filterButton)) {
      this.#selectGroup(filterButton.dataset.sampleFilter || '');
      return;
    }

    const removeButton = event.target.closest('[data-sample-remove-button]');
    if (!(removeButton instanceof HTMLButtonElement) || !this.contains(removeButton)) return;

    event.preventDefault();
    this.#removeSample(removeButton);
  };

  #selectGroup(groupName) {
    if (!groupName) return;

    this.querySelectorAll('[data-sample-filter]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.setAttribute('aria-pressed', button.dataset.sampleFilter === groupName ? 'true' : 'false');
    });

    this.querySelectorAll('[data-sample-group]').forEach((group) => {
      if (!(group instanceof HTMLElement)) return;
      group.hidden = group.dataset.sampleGroup !== groupName;
    });

    const list = this.querySelector('[data-sample-widget-list]');
    if (list instanceof HTMLElement) list.scrollTop = 0;
  }

  async #removeSample(removeButton) {
    if (removeButton.disabled) return;

    const variantId = String(removeButton.dataset.sampleVariantId || '');
    const cartKey = String(removeButton.dataset.sampleCartKey || '');
    if (!variantId || !cartKey || !this.#lastCart) {
      this.#showStatus('Não foi possível localizar esta amostra no carrinho. Tente novamente.');
      this.#syncCart();
      return;
    }

    const previousCart = this.#lastCart;
    this.#pendingRemoveVariants.add(variantId);
    this.#showStatus('');
    this.#applyCart(previousCart);

    const body = {
      id: cartKey,
      quantity: 0,
    };
    const sectionIds = cartSectionIds();
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname + window.location.search;
    }

    try {
      const response = await fetch(`${rootUrl()}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const cart = await response.json().catch(() => null);
      if (!response.ok || !Array.isArray(cart?.items)) {
        throw new Error(`Cart remove failed: ${response.status}`);
      }

      this.#pendingRemoveVariants.delete(variantId);
      this.#applyCart(cart);
      this.#renderCartBubble(cart.sections);
      this.#dispatchCartUpdate(cart, variantId, 'sample-widget-remove');
      this.#showStatus('Amostra removida do carrinho.', 'success');
    } catch (error) {
      console.warn('[FP] Sample widget remove failed.', error);
      this.#pendingRemoveVariants.delete(variantId);
      this.#applyCart(previousCart);
      this.#showStatus('Não foi possível remover esta amostra. Tente novamente.');
    }
  }

  #onCartUpdate = (event) => {
    if (this.dataset.sampleHydrated !== 'true') return;

    const cart = event.detail?.resource;
    if (Array.isArray(cart?.items)) {
      this.#applyCart(cart);
      return;
    }

    this.#syncCart();
  };

  #restoreFocus = () => {
    if (this.#suppressFocusRestore) return;
    if (this.#lastTrigger?.isConnected) this.#lastTrigger.focus();
  };

  #onDialogClose = () => {
    document.body.classList.remove('overflow-hidden');
    this.#restoreFocus();
  };

  #onPointerDown = (event) => {
    if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;
    if (!(event.target instanceof Element)) return;

    const handle = event.target.closest('[data-sample-widget-drag-handle]');
    if (!handle || !this.contains(handle) || !this.#dialog?.open) return;

    this.#dragPointerId = event.pointerId;
    this.#dragStartY = event.clientY;
    this.#dragDistance = 0;
    handle.setPointerCapture?.(event.pointerId);
    window.addEventListener('pointermove', this.#onPointerMove, { passive: false });
    window.addEventListener('pointerup', this.#onPointerEnd);
    window.addEventListener('pointercancel', this.#onPointerEnd);
  };

  #onPointerMove = (event) => {
    if (event.pointerId !== this.#dragPointerId) return;
    event.preventDefault();

    this.#dragDistance = Math.max(0, event.clientY - this.#dragStartY);
    this.#dialog?.classList.add('is-dragging');
    this.#dialog?.style.setProperty('--fp-sample-drag-y', `${this.#dragDistance}px`);
  };

  #onPointerEnd = (event) => {
    if (event.pointerId !== this.#dragPointerId) return;

    const dialog = this.#dialog;
    if (!dialog) return;

    const dialogHeight = dialog.getBoundingClientRect().height;
    const shouldClose = this.#dragDistance >= Math.max(DRAG_CLOSE_MIN_PX, dialogHeight * 0.25);

    this.#removeDragListeners();
    dialog.classList.remove('is-dragging');
    dialog.style.removeProperty('--fp-sample-drag-y');

    if (shouldClose) {
      this.close();
      return;
    }

    dialog.classList.add('is-snapping');
    window.setTimeout(() => dialog.classList.remove('is-snapping'), 200);
  };

  #removeDragListeners() {
    this.#dragPointerId = null;
    this.#dragDistance = 0;
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerEnd);
    window.removeEventListener('pointercancel', this.#onPointerEnd);
  }

  #onConnectedProductSwapped = async (event) => {
    const targetUrl = event.detail?.targetUrl;
    if (!targetUrl) return;

    const sequence = ++this.#refreshSequence;
    this.#refreshAbortController?.abort();
    this.#refreshAbortController = new AbortController();

    this.#suppressFocusRestore = true;
    this.close();
    this.#suppressFocusRestore = false;
    this.setAttribute('aria-busy', 'true');

    const timeoutId = window.setTimeout(() => this.#refreshAbortController?.abort(), REFRESH_TIMEOUT_MS);

    try {
      const requestUrl = new URL(targetUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId || '');

      const themeId = previewThemeId();
      if (themeId) requestUrl.searchParams.set('preview_theme_id', themeId);

      const response = await fetch(requestUrl, {
        headers: { Accept: 'text/html' },
        signal: this.#refreshAbortController.signal,
      });
      if (!response.ok) throw new Error(`Section refresh failed: ${response.status}`);

      const html = await response.text();
      if (sequence !== this.#refreshSequence) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextWidget = parsed.querySelector('fp-sample-widget[data-fp-sample-widget]');

      if (nextWidget) {
        this.replaceWith(nextWidget);
        return;
      }

      this.#hideStaleWidget();
    } catch (error) {
      if (sequence !== this.#refreshSequence) return;
      if (error?.name !== 'AbortError') {
        console.warn('[FP] Sample widget section refresh failed; hiding stale data.', error);
      }
      this.#hideStaleWidget();
    } finally {
      window.clearTimeout(timeoutId);
      if (sequence === this.#refreshSequence && this.isConnected) {
        this.removeAttribute('aria-busy');
      }
    }
  };

  #hideStaleWidget() {
    this.hidden = true;
    this.querySelector('[data-sample-widget-template]')?.remove();
    this.querySelector('[data-sample-widget-list]')?.replaceChildren();
  }
}

if (!customElements.get('fp-sample-widget')) {
  customElements.define('fp-sample-widget', FPSampleWidget);
}
